import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import TextArea from 'components/Input/Textarea';
import Table, { TableColumn } from 'components/Table';
import styled from 'styled-components/macro';
import Modal from 'components/Modal';
import actions from 'state/actions';
import { useDispatch } from 'react-redux';
import { useAsync } from 'react-use';
import { OpenPark } from '../../../../../models/response';

const NoteWrapper = styled.div`
  width: 100%;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 10px 20px;
`;
const Title = styled.p`
  margin: 20px 0 10px 0;
`;
const NoteMandatoryLabel = styled.span`
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
`;
const CloseParkDocument = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const [parkDocumentResponse, setParkDocumentResponse] = useState<OpenPark>();
  const [note, setNote] = useState<string>('');
  const { dateFormat, timeZone } = useSelector(selectors.user.getUserPreference);

  useAsync(async () => {
    try {
      if (protocol) {
        const { data } = await service.openParkDocument(protocol);
        setParkDocumentResponse(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const historyColumns: TableColumn[] = [
    {
      accessor: 'causalCode',
      Header: t('causalCode'),
    },
    { accessor: 'note', Header: t('note') },
    { accessor: 'protocol', Header: t('protocol') },
    {
      accessor: 'rejectionReason',
      Header: t('rejectionReason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, timeZone),
      filter: utils.date.epochFilterFunction(timeZone ?? 'UTC'),
      filterType: 'date',
      dateFormat,
    },
    {
      accessor: 'type',
      Header: t('type'),
    },
    {
      accessor: 'typeDescription',
      Header: t('typeDescription'),
    },
  ];

  const historyRows =
    parkDocumentResponse?.wfHistory.map((historyRecord) => ({
      causalCode: historyRecord.causalCode,
      note: historyRecord.note,
      protocol: historyRecord.protocol,
      rejectionReason: historyRecord.rejectionReason,
      sendDate: historyRecord.sendDate,
      type: historyRecord.type,
      typeDescription: historyRecord.typeDescription,
    })) || [];

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      if (protocol) {
        await service.closeCloseParkDocument({ protocol, note });
        dispatch(actions.documents.removeRows([protocol]));
        utils.app.notify('success', t('file_deparked'));
      }
    } catch (err) {
      console.error(err);
    }
    dispatch(actions.modal.closeModal());
  };

  return (
    <>
      <Modal.Header title={t('depark-document')} />
      <Modal.Content>
        {historyRows?.length ? (
          <>
            <Title>{t('storico')}</Title>
            <TableWrapper>
              <Table
                id="close-park-document-table"
                hasPagination
                hasSort
                hasFilter
                columns={historyColumns}
                rows={historyRows}
              />
            </TableWrapper>
          </>
        ) : null}
        <NoteWrapper>
          <Title>{t('note')}</Title>
          <TextArea name="Nota" placeholder={t('note')} onChange={handleChange} />
        </NoteWrapper>
        {parkDocumentResponse?.noteMandatory && note.length === 0 ? (
          <NoteMandatoryLabel>{t('note-obligatory')}</NoteMandatoryLabel>
        ) : null}
      </Modal.Content>
      <Modal.Footer
        confirmText={t('continue')}
        confirmAction={handleConfirm}
        confirmDisabled={parkDocumentResponse?.noteMandatory && note.length === 0}
      />
    </>
  );
};

export default CloseParkDocument;
